import styles from './styles.module.scss';
import { DrawerComponent } from 'components/layout/initializers/drawer-component';
import { createContext, useContext, useRef, useEffect, FC, useState, ReactNode } from 'react';

type Props = {
  itemId?: number | null | string;
  toggleDrawer: (id?: number | null | string) => void;
  setDrawerWidth: (width?: number) => void;
};
const DrawerContext = createContext<Props | null>(null);
const DEFAULT_DRAWER_WIDTH = 546;

type WrapperProps = {
  drawerName: string;
  children: ReactNode;
  wrapper: ReactNode;
};

export const DrawerWrapperComponent: FC<WrapperProps> = ({ children, wrapper, drawerName }) => {
  const [itemId, setItemId] = useState<number | null | string>();
  const drawerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!drawerRef.current) return;
    let drawerComponent = DrawerComponent.getInstance(drawerRef.current.id);

    if (!itemId) {
      drawerComponent?.hide();

      return;
    }

    if (!drawerComponent) {
      DrawerComponent.createInstances('#' + drawerRef.current.id);
      drawerComponent = DrawerComponent.getInstance(drawerRef.current.id);
    }

    drawerComponent?.show();
    handleChangeWidth(DEFAULT_DRAWER_WIDTH);
  }, [itemId]);

  const openDrawer = (id?: number | null | string) => {
    setItemId(id);
  };

  const handleChangeWidth = (width?: number) => {
    if (!drawerRef.current) return;

    const maxWidth = window.innerWidth;
    const finalWidth = Math.min(width ?? DEFAULT_DRAWER_WIDTH, maxWidth);

    drawerRef.current.style.width = `${finalWidth}px`;
  };

  return (
    <DrawerContext.Provider value={{ itemId: itemId, toggleDrawer: openDrawer, setDrawerWidth: handleChangeWidth }}>
      {children}
      <div
        ref={drawerRef}
        id={`${drawerName}-wrapper`}
        className={`bg-body ${styles.wrapper}`}
        data-kt-drawer="true"
        data-kt-drawer-name={`${drawerName}-wrapper`}
        data-kt-drawer-activate="true"
        data-kt-drawer-overlay="false"
        data-kt-drawer-width={`{default:'${DEFAULT_DRAWER_WIDTH}px'}`}
        data-kt-drawer-direction="end"
      >
        {/* begin::Card */}
        <div className="card shadow-none rounded-0 w-100">
          {/* begin::Body */}
          <div
            className="card-body"
            id={`${drawerName}-wrapper_body"`}
            style={{ paddingRight: '10px', overflow: 'hidden' }}
          >
            {/* begin::Content */}
            <div
              id={`kt_${drawerName}-wrapper_scroll`}
              className="scroll-y"
              data-kt-scroll="true"
              data-kt-scroll-height="auto"
              data-kt-scroll-wrappers={`#kt_${drawerName}-wrapper_body`}
              data-kt-scroll-dependencies={`kt_${drawerName}-wrapper_header`}
              data-kt-scroll-offset="5px"
              style={{ overflowX: 'hidden' }}
            >
              {wrapper}
            </div>
          </div>
        </div>
      </div>
    </DrawerContext.Provider>
  );
};

export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used inside DrawerProvider');
  }
  return context;
};
